import { STORAGE_KEYS } from '../config';

export const AUTH_SESSION_STORAGE_MODES = Object.freeze({
  PERSISTENT: 'persistent',
  SESSION: 'session',
});

const STORAGE_PRIORITY = [
  AUTH_SESSION_STORAGE_MODES.SESSION,
  AUTH_SESSION_STORAGE_MODES.PERSISTENT,
];

const USER_STORAGE_KEY = STORAGE_KEYS.user;
const SESSION_MODE_KEY = STORAGE_KEYS.authSessionMode;
const LEGACY_KEYS = [STORAGE_KEYS.token, STORAGE_KEYS.tokenExpires].filter(Boolean);

const isBrowserEnvironment = () => typeof window !== 'undefined';

const getStorageByMode = (storageMode) => {
  if (!isBrowserEnvironment()) return null;

  try {
    if (storageMode === AUTH_SESSION_STORAGE_MODES.SESSION) {
      return window.sessionStorage;
    }

    if (storageMode === AUTH_SESSION_STORAGE_MODES.PERSISTENT) {
      return window.localStorage;
    }
  } catch (error) {
    return null;
  }

  return null;
};

const readStorageValue = (storage, key) => {
  if (!storage || !key) return null;

  try {
    const value = storage.getItem(key);
    if (typeof value !== 'string') return null;
    const normalized = value.trim();
    return normalized || null;
  } catch (error) {
    return null;
  }
};

const writeStorageValue = (storage, key, value) => {
  if (!storage || !key) {
    throw new Error('Armazenamento indisponível no navegador.');
  }

  storage.setItem(key, value);
};

const removeStorageValue = (storage, key) => {
  if (!storage || !key) return;

  try {
    storage.removeItem(key);
  } catch (error) {
    // Session cleanup must be best-effort.
  }
};

const isValidStorageMode = (value) => STORAGE_PRIORITY.includes(value);

const warnStorageAnomaly = (reason, details = {}) => {
  if (process.env.NODE_ENV === 'test') return;
  console.warn('[auth-session-storage]', reason, details);
};

const clearStorageArtifacts = (storageMode) => {
  const storage = getStorageByMode(storageMode);
  removeStorageValue(storage, USER_STORAGE_KEY);
  removeStorageValue(storage, SESSION_MODE_KEY);
  LEGACY_KEYS.forEach((key) => removeStorageValue(storage, key));
};

const snapshotStorageArtifacts = (storageMode) => {
  const storage = getStorageByMode(storageMode);

  return {
    storageMode,
    userJson: readStorageValue(storage, USER_STORAGE_KEY),
    modeMarker: readStorageValue(storage, SESSION_MODE_KEY),
  };
};

const restoreStorageArtifacts = (snapshot) => {
  if (!snapshot) return;

  const storage = getStorageByMode(snapshot.storageMode);
  if (!storage) return;

  clearStorageArtifacts(snapshot.storageMode);

  try {
    if (snapshot.userJson) {
      writeStorageValue(storage, USER_STORAGE_KEY, snapshot.userJson);
    }
  } catch (error) {
    clearStorageArtifacts(snapshot.storageMode);
    warnStorageAnomaly('failed_to_restore_previous_auth_user_cache', {
      storageMode: snapshot.storageMode,
    });
    return;
  }

  if (!snapshot.modeMarker) {
    return;
  }

  try {
    writeStorageValue(storage, SESSION_MODE_KEY, snapshot.modeMarker);
  } catch (error) {
    warnStorageAnomaly('failed_to_restore_previous_auth_session_mode', {
      storageMode: snapshot.storageMode,
    });
  }
};

const parseStoredUser = (rawJson) => {
  if (!rawJson) return null;

  try {
    const parsed = JSON.parse(rawJson);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const readStorageState = (storageMode) => {
  const storage = getStorageByMode(storageMode);
  const userJson = readStorageValue(storage, USER_STORAGE_KEY);
  const modeMarker = readStorageValue(storage, SESSION_MODE_KEY);
  const parsedUser = parseStoredUser(userJson);

  return {
    storageMode,
    userJson,
    userPayload: parsedUser,
    modeMarker: isValidStorageMode(modeMarker) ? modeMarker : null,
    hasUserPayload: Boolean(parsedUser),
    hasAnyAuthArtifact: Boolean(userJson || modeMarker),
  };
};

const sanitizeStorageState = (state) => {
  if (!state.hasAnyAuthArtifact) {
    return state;
  }

  if (state.hasUserPayload) {
    return state;
  }

  warnStorageAnomaly('invalid_auth_user_cache_cleared', {
    storageMode: state.storageMode,
    hasUserJson: Boolean(state.userJson),
    hasModeMarker: Boolean(state.modeMarker),
  });

  clearStorageArtifacts(state.storageMode);
  return readStorageState(state.storageMode);
};

const resolveStoredAuthUserState = () => {
  const sanitizedStates = STORAGE_PRIORITY.map((storageMode) =>
    sanitizeStorageState(readStorageState(storageMode))
  );

  const validStates = sanitizedStates.filter((state) => state.hasUserPayload);
  if (validStates.length === 0) {
    return null;
  }

  if (validStates.length > 1) {
    const [preferredState, ...staleStates] = validStates;

    warnStorageAnomaly('multiple_auth_user_caches_detected', {
      preferredStorageMode: preferredState.storageMode,
      staleStorageModes: staleStates.map((state) => state.storageMode),
    });

    staleStates.forEach((state) => clearStorageArtifacts(state.storageMode));

    return {
      user: preferredState.userPayload,
      storageMode: preferredState.storageMode,
      rememberMe: preferredState.storageMode === AUTH_SESSION_STORAGE_MODES.PERSISTENT,
    };
  }

  const [resolvedState] = validStates;
  return {
    user: resolvedState.userPayload,
    storageMode: resolvedState.storageMode,
    rememberMe: resolvedState.storageMode === AUTH_SESSION_STORAGE_MODES.PERSISTENT,
  };
};

const validateUserPayload = (user) => {
  if (!user || typeof user !== 'object' || Array.isArray(user)) {
    throw new Error('Cache de autenticação inválido para persistência.');
  }

  return JSON.stringify(user);
};

const persistUserJsonToMode = ({ userJson, storageMode }) => {
  const storage = getStorageByMode(storageMode);
  if (!storage) {
    warnStorageAnomaly('auth_user_cache_storage_unavailable', { storageMode });
    return {
      persisted: false,
      storageMode,
      rememberMe: storageMode === AUTH_SESSION_STORAGE_MODES.PERSISTENT,
    };
  }

  const otherStorageMode = storageMode === AUTH_SESSION_STORAGE_MODES.SESSION
    ? AUTH_SESSION_STORAGE_MODES.PERSISTENT
    : AUTH_SESSION_STORAGE_MODES.SESSION;
  const targetSnapshot = snapshotStorageArtifacts(storageMode);

  try {
    writeStorageValue(storage, SESSION_MODE_KEY, storageMode);
    writeStorageValue(storage, USER_STORAGE_KEY, userJson);
  } catch (error) {
    restoreStorageArtifacts(targetSnapshot);
    warnStorageAnomaly('failed_to_persist_auth_user_cache', { storageMode });
    return {
      persisted: false,
      storageMode,
      rememberMe: storageMode === AUTH_SESSION_STORAGE_MODES.PERSISTENT,
    };
  }

  clearStorageArtifacts(otherStorageMode);

  return {
    persisted: true,
    storageMode,
    rememberMe: storageMode === AUTH_SESSION_STORAGE_MODES.PERSISTENT,
  };
};

export const getStoredAuthUser = () => resolveStoredAuthUserState();

export const persistStoredAuthUser = (user, { rememberMe = true } = {}) => {
  const userJson = validateUserPayload(user);
  const storageMode = rememberMe
    ? AUTH_SESSION_STORAGE_MODES.PERSISTENT
    : AUTH_SESSION_STORAGE_MODES.SESSION;

  return persistUserJsonToMode({ userJson, storageMode });
};

export const refreshStoredAuthUser = (user) => {
  const userJson = validateUserPayload(user);
  const currentSession = resolveStoredAuthUserState();
  const storageMode = currentSession?.storageMode || AUTH_SESSION_STORAGE_MODES.PERSISTENT;

  return persistUserJsonToMode({ userJson, storageMode });
};

export const clearStoredAuthUser = () => {
  clearStorageArtifacts(AUTH_SESSION_STORAGE_MODES.SESSION);
  clearStorageArtifacts(AUTH_SESSION_STORAGE_MODES.PERSISTENT);
};
